import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface JsonTreeProps {
  data: unknown;
  /** Depth at which nodes start expanded. */
  defaultOpenDepth?: number;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function Primitive({ value }: { value: unknown }) {
  if (value === null) return <span className="text-oem-400 italic">null</span>;
  if (value === undefined) return <span className="text-oem-400 italic">undefined</span>;
  switch (typeof value) {
    case 'string':
      return <span className="text-accent-700">"{value}"</span>;
    case 'number':
      return <span className="text-oem-800 font-medium">{String(value)}</span>;
    case 'boolean':
      return <span className="text-status-abandoned font-medium">{String(value)}</span>;
    default:
      return <span className="text-oem-700">{String(value)}</span>;
  }
}

function Node({ name, value, depth, defaultOpenDepth }: { name: string; value: unknown; depth: number; defaultOpenDepth: number }) {
  const [open, setOpen] = useState(depth < defaultOpenDepth);
  const expandable = isObject(value) || Array.isArray(value);

  if (!expandable) {
    return (
      <div className="flex items-baseline gap-2 py-px" style={{ paddingLeft: depth * 16 }}>
        <span className="text-oem-500 shrink-0">{name}:</span>
        <span className="break-all"><Primitive value={value} /></span>
      </div>
    );
  }

  const entries: Array<[string, unknown]> = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as [string, unknown])
    : Object.entries(value);
  const summary = Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`;

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 py-px hover:bg-oem-100 rounded px-0.5 -ml-0.5 w-full text-left"
        style={{ paddingLeft: depth * 16 }}
      >
        {open ? (
          <ChevronDown className="w-3 h-3 text-oem-400 shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-oem-400 shrink-0" />
        )}
        <span className="text-oem-500">{name}:</span>
        {!open && <span className="text-oem-400 text-[11px]">{summary}</span>}
        {open && <span className="text-oem-400">{Array.isArray(value) ? '[' : '{'}</span>}
      </button>
      {open && (
        <>
          {entries.map(([k, v]) => (
            <Node key={k} name={k} value={v} depth={depth + 1} defaultOpenDepth={defaultOpenDepth} />
          ))}
          <div className="text-oem-400" style={{ paddingLeft: depth * 16 + 16 }}>
            {Array.isArray(value) ? ']' : '}'}
          </div>
        </>
      )}
    </div>
  );
}

/** Collapsible, syntax-tinted JSON tree for inspecting raw event payloads. */
export function JsonTree({ data, defaultOpenDepth = 1 }: JsonTreeProps) {
  return (
    <div className="font-mono text-xs leading-relaxed bg-oem-50 border border-oem-200 rounded-lg p-3 overflow-x-auto">
      <Node name="event" value={data} depth={0} defaultOpenDepth={defaultOpenDepth} />
    </div>
  );
}
